// src/components/Dashboard/StatusTab.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { collection, doc, getDoc, getDocs, getFirestore } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  BarChart3,
  RefreshCw,
  Users,
  Home,
  Phone,
  CheckCircle,
  XCircle,
  TrendingUp
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line
} from 'recharts';

const db = getFirestore(app);

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899'];

function toMs(ts) {
  if (!ts) return 0;
  if (ts.toMillis) return ts.toMillis();
  if (typeof ts === 'number') return ts;
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
}

export default function StatusTab() {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [igloos, setIgloos] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [signedInFamilies, setSignedInFamilies] = useState(new Set());
  const [familyFirstEventTime, setFamilyFirstEventTime] = useState({});
  const [selectedBatch, setSelectedBatch] = useState('all');
  const [expandedGraphs, setExpandedGraphs] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [accountsSnap, igloosSnap, sessionsSnap] = await Promise.all([
        getDocs(collection(db, 'accountsInfo')),
        getDocs(collection(db, 'iglooInfo')),
        getDocs(collection(db, 'iglooSessionsHistory'))
      ]);

      const accountsList = accountsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAccounts(accountsList);
      setIgloos(igloosSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSessions(sessionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Collect all family IDs from accounts
      const allFamilyIds = new Set();
      accountsList.forEach(a => (a.families || []).forEach(fid => allFamilyIds.add(fid)));

      // Read firstSignin from familyInfo for each family
      const signedIn = new Set();
      const firstEvents = {};

      await Promise.all(
        Array.from(allFamilyIds).map(async (familyId) => {
          try {
            const familySnap = await getDoc(doc(db, 'familyInfo', familyId));
            if (familySnap.exists() && familySnap.data().firstSignin) {
              signedIn.add(familyId);
              firstEvents[familyId] = toMs(familySnap.data().firstSignin);
            }
          } catch (e) { /* ignore */ }
        })
      );

      setSignedInFamilies(signedIn);
      setFamilyFirstEventTime(firstEvents);
    } catch (err) {
      console.error('StatusTab: failed to fetch data', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Batch list
  const batches = useMemo(() => {
    const set = new Set();
    accounts.forEach(a => { if (a.batch) set.add(a.batch); });
    return Array.from(set).sort();
  }, [accounts]);

  // Filter accounts by batch
  const filteredAccounts = useMemo(() => {
    if (selectedBatch === 'all') return accounts;
    return accounts.filter(a => a.batch === selectedBatch);
  }, [accounts, selectedBatch]);

  const filteredAccountIds = useMemo(
    () => new Set(filteredAccounts.map(a => a.id)),
    [filteredAccounts]
  );

  // Build a map: familyId -> accountId
  const familyToAccount = useMemo(() => {
    const map = {};
    filteredAccounts.forEach(a => {
      (a.families || []).forEach(fid => { map[fid] = a.id; });
    });
    return map;
  }, [filteredAccounts]);

  // Filtered families (all familyIds belonging to filtered accounts)
  const filteredFamilyIds = useMemo(
    () => new Set(Object.keys(familyToAccount)),
    [familyToAccount]
  );

  // Family sign-in status: signed in if onlinePing exists in deviceManagement OR any deviceEvent exists
  const familySignedInStatus = useMemo(() => {
    const result = new Set();
    filteredFamilyIds.forEach(fid => {
      if (signedInFamilies.has(fid)) result.add(fid);
    });
    return result;
  }, [filteredFamilyIds, signedInFamilies]);

  // Which families have joined any session, and which have joined a multi-family session
  const { familiesWithSession, familiesWithMultiFamilySession } = useMemo(() => {
    const any = new Set();
    const multi = new Set();
    sessions.forEach(s => {
      const familyIds = s.participantFamilyIds || [];
      const peak = s.peakConcurrent || 0;
      familyIds.forEach(fid => {
        if (filteredFamilyIds.has(fid)) {
          any.add(fid);
          if (peak >= 2) multi.add(fid);
        }
      });
    });
    return { familiesWithSession: any, familiesWithMultiFamilySession: multi };
  }, [sessions, filteredFamilyIds]);

  // --- Computed stats ---
  const stats = useMemo(() => {
    const totalAccounts = filteredAccounts.length;
    const totalFamilies = filteredAccounts.reduce((sum, a) => sum + (a.numOfFamilies || 0), 0);
    const setupCompleted = filteredAccounts.filter(a => a.accountStatus === 'setupCompleted').length;
    const setupCompletedAccounts = filteredAccounts.filter(a => a.accountStatus === 'setupCompleted');

    // Account sign-in breakdown (of setupCompleted accounts)
    let acctNoFamiliesSignedIn = 0;
    let acctExactlyOneFamilySignedIn = 0;
    let acctMoreThanOneFamilySignedIn = 0;
    let acctHasUnsignedFamilies = 0;

    setupCompletedAccounts.forEach(a => {
      const families = a.families || [];
      const total = a.numOfFamilies || families.length || 0;
      const signedInCount = families.filter(fid => familySignedInStatus.has(fid)).length;
      if (signedInCount === 0) acctNoFamiliesSignedIn++;
      else if (signedInCount === 1) acctExactlyOneFamilySignedIn++;
      else acctMoreThanOneFamilySignedIn++;
      if (signedInCount < total) acctHasUnsignedFamilies++;
    });

    // Account session stats
    const accountsWithSession = new Set();
    const accountsWithMultiFamilySession = new Set();
    const accountAllFamiliesSessioned = new Set();

    // First pass: track which families per account have sessioned
    const acctFamiliesSessioned = {};
    sessions.forEach(s => {
      const familyIds = s.participantFamilyIds || [];
      const peak = s.peakConcurrent || 0;
      familyIds.forEach(fid => {
        const accId = familyToAccount[fid];
        if (!accId) return;
        accountsWithSession.add(accId);
        if (peak >= 2) accountsWithMultiFamilySession.add(accId);
        if (!acctFamiliesSessioned[accId]) acctFamiliesSessioned[accId] = new Set();
        acctFamiliesSessioned[accId].add(fid);
      });
    });

    // Check which accounts have ALL families sessioned
    filteredAccounts.forEach(a => {
      const families = a.families || [];
      if (families.length === 0) return;
      const sessioned = acctFamiliesSessioned[a.id];
      if (sessioned && families.every(fid => sessioned.has(fid))) {
        accountAllFamiliesSessioned.add(a.id);
      }
    });

    // Family-level stats
    const familiesSignedIn = familySignedInStatus.size;
    const familiesInSession = familiesWithSession.size;
    const familiesInMultiFamilySession = familiesWithMultiFamilySession.size;

    // Unsigned families breakdown: by account setup status
    let unsignedFamAcctSetup = 0;
    let unsignedFamAcctNotSetup = 0;
    filteredAccounts.forEach(a => {
      const acctSetupCompleted = a.accountStatus === 'setupCompleted';
      if (acctSetupCompleted) {
        const families = a.families || [];
        families.forEach(fid => {
          if (!familySignedInStatus.has(fid)) unsignedFamAcctSetup++;
        });
      } else {
        unsignedFamAcctNotSetup += (a.numOfFamilies || 0);
      }
    });

    return {
      totalAccounts,
      totalFamilies,
      setupCompleted,
      acctNoFamiliesSignedIn,
      acctExactlyOneFamilySignedIn,
      acctMoreThanOneFamilySignedIn,
      acctHasUnsignedFamilies,
      accountsWithSession: accountsWithSession.size,
      accountsWithMultiFamilySession: accountsWithMultiFamilySession.size,
      accountAllFamiliesSessioned: accountAllFamiliesSessioned.size,
      familiesSignedIn,
      unsignedFamAcctSetup,
      unsignedFamAcctNotSetup,
      familiesInSession,
      familiesInMultiFamilySession,
    };
  }, [filteredAccounts, familySignedInStatus, sessions, familyToAccount, familiesWithSession, familiesWithMultiFamilySession]);

  // Pie chart data: account setup funnel
  const funnelData = useMemo(() => {
    const notSetup = stats.totalAccounts - stats.setupCompleted;
    const items = [];
    if (notSetup > 0) items.push({ name: 'Not Setup', value: notSetup });
    if (stats.acctNoFamiliesSignedIn > 0) items.push({ name: 'Setup, 0 Signed In', value: stats.acctNoFamiliesSignedIn });
    if (stats.acctExactlyOneFamilySignedIn > 0) items.push({ name: 'Setup, 1 Signed In', value: stats.acctExactlyOneFamilySignedIn });
    if (stats.acctMoreThanOneFamilySignedIn > 0) items.push({ name: 'Setup, 2+ Signed In', value: stats.acctMoreThanOneFamilySignedIn });
    return items;
  }, [stats]);

  const sessionFunnelData = useMemo(() => {
    const noSession = stats.setupCompleted - stats.accountsWithSession;
    const singleOnly = stats.accountsWithSession - stats.accountsWithMultiFamilySession;
    const items = [];
    if (noSession > 0) items.push({ name: 'No Sessions', value: noSession });
    if (singleOnly > 0) items.push({ name: 'Solo Sessions Only', value: singleOnly });
    if (stats.accountsWithMultiFamilySession > 0) items.push({ name: 'Multi-Family Sessions', value: stats.accountsWithMultiFamilySession });
    return items;
  }, [stats]);

  // New family sign-ins per week (using earliest deviceEvent timestamp per family)
  const signInsPerWeek = useMemo(() => {
    const timestamps = [];
    filteredFamilyIds.forEach(fid => {
      const ts = familyFirstEventTime[fid];
      if (ts) timestamps.push(ts);
    });
    if (timestamps.length === 0) return [];

    const minTs = Math.min(...timestamps);
    const maxTs = Math.max(...timestamps);
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const buckets = [];
    let bucketStart = minTs - (minTs % weekMs);

    while (bucketStart <= maxTs + weekMs) {
      buckets.push({ start: bucketStart, end: bucketStart + weekMs, newSignIns: 0 });
      bucketStart += weekMs;
    }

    timestamps.forEach(ts => {
      const bucket = buckets.find(b => ts >= b.start && ts < b.end);
      if (bucket) bucket.newSignIns++;
    });

    // Calculate remaining (total families minus cumulative sign-ins so far)
    const totalFams = stats.totalFamilies;
    let cumulative = 0;
    return buckets.map(b => {
      cumulative += b.newSignIns;
      return {
        week: new Date(b.start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
        newSignIns: b.newSignIns,
        remaining: totalFams - cumulative,
      };
    });
  }, [filteredFamilyIds, familyFirstEventTime, stats.totalFamilies]);

  // Total sessions per week
  const totalSessionsPerWeek = useMemo(() => {
    if (sessions.length === 0) return [];

    const relevantSessions = sessions.filter(s => {
      const familyIds = s.participantFamilyIds || [];
      return familyIds.some(fid => familyToAccount[fid]);
    });

    if (relevantSessions.length === 0) return [];

    const timestamps = relevantSessions.map(s => toMs(s.startedAt)).filter(t => t > 0);
    if (timestamps.length === 0) return [];

    const minTs = Math.min(...timestamps);
    const maxTs = Math.max(...timestamps);
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const buckets = [];
    let bucketStart = minTs - (minTs % weekMs);

    while (bucketStart <= maxTs + weekMs) {
      buckets.push({ start: bucketStart, end: bucketStart + weekMs, total: 0, multiFam: 0 });
      bucketStart += weekMs;
    }

    relevantSessions.forEach(s => {
      const ts = toMs(s.startedAt);
      if (ts <= 0) return;
      const bucket = buckets.find(b => ts >= b.start && ts < b.end);
      if (!bucket) return;
      bucket.total++;
      if ((s.peakConcurrent || 0) >= 2) bucket.multiFam++;
    });

    return buckets.map(b => ({
      week: new Date(b.start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      total: b.total,
      multiFamily: b.multiFam,
    }));
  }, [sessions, familyToAccount]);

  // Sessions per day (last 8 days)
  const sessionsPerDay = useMemo(() => {
    if (sessions.length === 0) return [];
    const dayMs = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const startDay = Math.floor(now / dayMs) - 7; // 8 days: today + 7 prior

    const relevantSessions = sessions.filter(s => {
      const familyIds = s.participantFamilyIds || [];
      return familyIds.some(fid => familyToAccount[fid]);
    });

    const buckets = [];
    for (let d = startDay; d <= Math.floor(now / dayMs); d++) {
      buckets.push({ dayKey: d, start: d * dayMs, end: (d + 1) * dayMs, total: 0, multiFam: 0 });
    }

    relevantSessions.forEach(s => {
      const ts = toMs(s.startedAt);
      if (ts <= 0) return;
      const dayKey = Math.floor(ts / dayMs);
      const bucket = buckets.find(b => b.dayKey === dayKey);
      if (!bucket) return;
      bucket.total++;
      if ((s.peakConcurrent || 0) >= 2) bucket.multiFam++;
    });

    return buckets.map(b => ({
      day: new Date(b.start).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }),
      total: b.total,
      multiFamily: b.multiFam,
    }));
  }, [sessions, familyToAccount]);

  // Family sign-ins per day (last 8 days)
  const signInsPerDay = useMemo(() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const startDay = Math.floor(now / dayMs) - 7;

    const buckets = [];
    for (let d = startDay; d <= Math.floor(now / dayMs); d++) {
      buckets.push({ dayKey: d, start: d * dayMs, end: (d + 1) * dayMs, newSignIns: 0 });
    }

    // Count sign-ins per day bucket
    filteredFamilyIds.forEach(fid => {
      const ts = familyFirstEventTime[fid];
      if (!ts) return;
      const dayKey = Math.floor(ts / dayMs);
      const bucket = buckets.find(b => b.dayKey === dayKey);
      if (bucket) bucket.newSignIns++;
    });

    // For remaining, we need cumulative sign-ins up to each day
    const totalFams = stats.totalFamilies;
    // Count all sign-ins before the window
    let cumulativeBefore = 0;
    filteredFamilyIds.forEach(fid => {
      const ts = familyFirstEventTime[fid];
      if (ts && Math.floor(ts / dayMs) < startDay) cumulativeBefore++;
    });

    let cumulative = cumulativeBefore;
    return buckets.map(b => {
      cumulative += b.newSignIns;
      return {
        day: new Date(b.start).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' }),
        newSignIns: b.newSignIns,
        remaining: totalFams - cumulative,
      };
    });
  }, [filteredFamilyIds, familyFirstEventTime, stats.totalFamilies]);

  const toggleGraph = (key) => {
    setExpandedGraphs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const StatRow = ({ label, value, graphKey, children }) => (
    <>
      <tr className="border-b hover:bg-muted/50 transition-colors">
        <td className="py-2 px-3 text-sm">{label}</td>
        <td className="py-2 px-3 text-sm font-mono text-right font-medium">{value}</td>
        <td className="py-2 px-3 text-right">
          {children && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
              onClick={() => toggleGraph(graphKey)}>
              <TrendingUp className="w-3 h-3 mr-1" />
              {expandedGraphs[graphKey] ? 'Hide' : 'Graph'}
            </Button>
          )}
        </td>
      </tr>
      {expandedGraphs[graphKey] && children && (
        <tr>
          <td colSpan={3} className="p-4">{children}</td>
        </tr>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {/* Header: Batch filter + refresh */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Batch:</span>
          <Select value={selectedBatch} onValueChange={setSelectedBatch}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Batches</SelectItem>
              {batches.map(b => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
        {!loading && accounts.length > 0 && (
          <span className="text-xs text-muted-foreground">
            Data loaded: {accounts.length} accounts, {signedInFamilies.size} signed-in families, {sessions.length} sessions
          </span>
        )}
      </div>

      {loading && accounts.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin" />
          <p>Loading fleet status...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Row 1: Fleet Summary (left) + two pie charts stacked (right) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Summary Stats Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="w-4 h-4" />
                  Fleet Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Accounts section */}
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Accounts
                </div>
                <table className="w-full mb-5">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1.5 px-3 text-xs font-medium text-muted-foreground">Metric</th>
                      <th className="text-right py-1.5 px-3 text-xs font-medium text-muted-foreground">Count</th>
                      <th className="text-right py-1.5 px-3 text-xs font-medium text-muted-foreground w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <StatRow label="Total Accounts" value={stats.totalAccounts} />
                    <StatRow label={<span className="flex items-center gap-2"><XCircle className="w-3.5 h-3.5 text-red-500" /> Not Setup</span>} value={stats.totalAccounts - stats.setupCompleted} />
                    <StatRow label={<span className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500" /> Setup Completed</span>} value={stats.setupCompleted} />
                    <StatRow label={<span className="pl-6 text-muted-foreground">0 families signed in</span>} value={stats.acctNoFamiliesSignedIn} />
                    <StatRow label={<span className="pl-6 text-muted-foreground">Exactly 1 family signed in</span>} value={stats.acctExactlyOneFamilySignedIn} />
                    <StatRow label={<span className="pl-6 text-muted-foreground">2+ families signed in</span>} value={stats.acctMoreThanOneFamilySignedIn} />
                    <StatRow label={<span className="pl-6 text-muted-foreground">Has families not yet signed in</span>} value={stats.acctHasUnsignedFamilies} />
                    <StatRow
                      label={<span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-blue-500" /> At least 1 family sessioned</span>}
                      value={stats.accountsWithSession}
                    />
                    <StatRow
                      label={<span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-purple-500" /> At least 1 multi-family session</span>}
                      value={stats.accountsWithMultiFamilySession}
                    />
                    <StatRow
                      label={<span className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-cyan-500" /> All families have sessioned</span>}
                      value={stats.accountAllFamiliesSessioned}
                    />
                  </tbody>
                </table>

                {/* Families section */}
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1.5">
                  <Home className="w-3.5 h-3.5" /> Families
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1.5 px-3 text-xs font-medium text-muted-foreground">Metric</th>
                      <th className="text-right py-1.5 px-3 text-xs font-medium text-muted-foreground">Count</th>
                      <th className="text-right py-1.5 px-3 text-xs font-medium text-muted-foreground w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <StatRow label="Total Families" value={stats.totalFamilies} />
                    <StatRow label={<span className="flex items-center gap-2"><XCircle className="w-3.5 h-3.5 text-red-500" /> Not Signed In</span>} value={stats.unsignedFamAcctSetup + stats.unsignedFamAcctNotSetup} />
                    <StatRow label={<span className="pl-6 text-muted-foreground">Account not setup</span>} value={stats.unsignedFamAcctNotSetup} />
                    <StatRow label={<span className="pl-6 text-muted-foreground">Account setup completed</span>} value={stats.unsignedFamAcctSetup} />
                    <StatRow label={<span className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-green-500" /> Signed In</span>} value={stats.familiesSignedIn} />
                    <StatRow label="Joined an Igloo Session" value={stats.familiesInSession} />
                    <StatRow label="Joined a Multi-Family Session" value={stats.familiesInMultiFamilySession} />
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Right column: two pie charts stacked */}
            <div className="flex flex-col gap-6">
              {/* Account Setup Breakdown */}
              <Card>
                <CardHeader className="pb-1">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Users className="w-4 h-4" />
                    Account Setup Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {funnelData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart margin={{ top: 20, bottom: 10 }}>
                        <Pie
                          data={funnelData}
                          cx="50%"
                          cy="48%"
                          outerRadius={75}
                          dataKey="value"
                          label={({ value }) => value}
                          labelLine={false}
                        >
                          {funnelData.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground text-sm">No data</div>
                  )}
                </CardContent>
              </Card>

              {/* Session Engagement Breakdown */}
              <Card>
                <CardHeader className="pb-1">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Phone className="w-4 h-4" />
                    Session Engagement Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {sessionFunnelData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart margin={{ top: 20, bottom: 10 }}>
                        <Pie
                          data={sessionFunnelData}
                          cx="50%"
                          cy="48%"
                          outerRadius={75}
                          dataKey="value"
                          label={({ value }) => value}
                          labelLine={false}
                        >
                          {sessionFunnelData.map((_, i) => (
                            <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center py-6 text-muted-foreground text-sm">No data</div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Row 2: Sessions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sessions Per Week */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-4 h-4" />
                  Sessions Per Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                {totalSessionsPerWeek.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={totalSessionsPerWeek}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total" name="All Sessions" fill="#6366f1" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="multiFamily" name="Multi-Family" fill="#22c55e" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">No session data</div>
                )}
              </CardContent>
            </Card>

            {/* Sessions Per Day (Last 8 Days) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-4 h-4" />
                  Sessions Per Day (Last 8 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sessionsPerDay.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={sessionsPerDay}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total" name="All Sessions" fill="#6366f1" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="multiFamily" name="Multi-Family" fill="#22c55e" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">No session data</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Family Sign-Ins */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Family Sign-Ins Per Week */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Home className="w-4 h-4" />
                  Family Sign-Ins Per Week
                </CardTitle>
              </CardHeader>
              <CardContent>
                {signInsPerWeek.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={signInsPerWeek}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="newSignIns" name="New Sign-Ins" fill="#22c55e" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="remaining" name="Remaining" fill="#ef4444" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">No sign-in data</div>
                )}
              </CardContent>
            </Card>

            {/* Family Sign-Ins Per Day (Last 8 Days) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Home className="w-4 h-4" />
                  Family Sign-Ins Per Day (Last 8 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {signInsPerDay.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={signInsPerDay}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="newSignIns" name="New Sign-Ins" fill="#22c55e" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="remaining" name="Remaining" fill="#ef4444" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-8 text-muted-foreground text-sm">No sign-in data</div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
